"use client";

import { useConnectWallet, useSetChain } from "@web3-onboard/react";
import { useEffect } from "react";

import { CHAINS } from "@/constants";
import { Chain } from "@/types";

export const useChain = (): {
  chain: Chain;
  isBusy: boolean;
  setChain: typeof setChain;
} => {
  const [{ wallet }] = useConnectWallet();
  const [{ connectedChain, settingChain }, setChain] = useSetChain();
  const chain = connectedChain
    ? CHAINS.find(({ id }) => id === BigInt(connectedChain.id)) ?? CHAINS[0]
    : CHAINS[0];

  useEffect(() => {
    if (
      wallet &&
      connectedChain &&
      !CHAINS.find(({ id }) => id === BigInt(connectedChain.id))
    ) {
      setTimeout(async () => {
        const targetChain = CHAINS[0];
        const chainIdHex = `0x${targetChain.id.toString(16)}`;

        if (!wallet.provider) {
          return console.warn("No wallet provider available");
        }

        try {
          console.log("Attempting to switch to chain:", chainIdHex);

          // Try to switch using wallet provider directly
          await wallet.provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: chainIdHex }],
          });

          console.log("Successfully switched to chain");
        } catch (switchError: unknown) {
          console.log("Switch failed, trying to add chain:", switchError);

          // If switching fails because chain doesn't exist (error 4902), try to add it
          const hasErrorCode =
            switchError &&
            typeof switchError === "object" &&
            "code" in switchError &&
            (switchError as { code: number }).code === 4902;

          if (hasErrorCode) {
            try {
              console.log("Adding chain to wallet...");

              await wallet.provider.request({
                method: "wallet_addEthereumChain",
                params: [
                  {
                    blockExplorerUrls: ["https://arbiscan.io/"],
                    chainId: chainIdHex,
                    chainName: targetChain.title,
                    nativeCurrency: {
                      decimals: 18,
                      name: targetChain.token,
                      symbol: targetChain.token,
                    },
                    rpcUrls: [targetChain.url.replace("wss://", "https://")],
                  },
                ],
              });

              console.log("Successfully added chain");
            } catch (addError) {
              console.error("Failed to add chain:", addError);

              alert(
                `Please manually add the ${targetChain.title} network to your wallet.\n\n` +
                  `Network: ${targetChain.title}\n` +
                  `Chain ID: ${targetChain.id}\n` +
                  `RPC URL: ${targetChain.url.replace("wss://", "https://")}`
              );
            }
          } else {
            console.error("Failed to switch chain:", switchError);

            alert(
              `Please manually switch your wallet to the ${targetChain.title} network to use this app.`
            );
          }
        }
      }, 1000);
    }
  }, [connectedChain, wallet]);

  return {
    chain,
    isBusy: settingChain,
    setChain,
  };
};
